'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useSessionStorage } from 'usehooks-ts';
import { Row, Col, Input, ListGroup, Button } from 'reactstrap';
import { MdOutlineFilterAlt, MdOutlineSave } from 'react-icons/md';
import FacetSideNav from './FacetSideNav';
import JobTeaser from './JobTeaser';
import Pager from './Pager';
import JobSearchForm from './JobSearchForm';
import FilterSummary from './FilterSummary';
import JobFacetFilters from './JobFacetFilters';
import NoResults from './svg/NoResults';

import JobSearchSaveModal from './JobSearchSaveModal';
import { useUser } from '../contexts/UserContext';
import LoginModal from './LoginModal';
import JobsearchSavedAlert from './mySynergie/JobsearchSavedAlert';
import { getI18nUtils } from '../i18n/i18n-utils';
import { convertSearchParamsToJobSearchQuery, facetGroupNames, JobsSearchResponse } from '../api/jobs';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import querystring from 'query-string';

function Jobs({
  jobData,
  dictionary,
  locale,
  nextPageSearchParams,
}: {
  nextPageSearchParams: NextPageSearchParams;
  jobData: JobsSearchResponse;
  dictionary: Partial<Dictionaries>;
  locale: Locale;
}): JSX.Element {
  const searchParams = useSearchParams();
  const { push } = useRouter();
  const { formatMessage } = getI18nUtils(dictionary);
  const { user } = useUser();

  const [facetSideNavOpen, setFacetSideNavOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [, setSessionJobFacetFilters] = useSessionStorage<Record<string, any> | null>('jobFacetFilters', null);

  const vacancyParams: Record<string, string[]> = {};
  facetGroupNames.forEach((param) => (vacancyParams[param] = searchParams?.getAll(param)));

  const toggleSideNav = () => setFacetSideNavOpen((prev) => !prev);
  const toggleSaveModal = () => setSaveModalOpen((prev) => !prev);
  const toggleLoginModal = () => setLoginModalOpen((prev) => !prev);

  const includeFilters = [
    'contractTypes',
    'division',
    'experiences',
    'jobDomains',
    'laborRegimes',
    'locations',
    'shifts',
    'workPlaceType',
    'student',
  ];

  const activeFilters: string[] = facetGroupNames
    .filter((gn) => includeFilters.includes(gn))
    .map((gn) => searchParams?.getAll(gn))
    .flat();
  const filterCount = activeFilters.length;

  const handleClickSave = () => {
    if (!user) toggleLoginModal();
    else toggleSaveModal();
  };

  // When a user has logged in via the loginModal this means this user tried
  // to save a search so login modal should be closed and save modal opened
  useEffect(() => {
    if (user && loginModalOpen) {
      toggleLoginModal();
      toggleSaveModal();
    }
  }, [user]);

  useEffect(() => {
    setSessionJobFacetFilters(convertSearchParamsToJobSearchQuery(nextPageSearchParams, locale, { exclude: ['page'] }));
  }, [nextPageSearchParams]);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const firstJob = jobData?.count ? currentPage * 10 - 9 : 0;
  const lastJob = currentPage * 10 > jobData?.count ? jobData?.count : currentPage * 10;

  return (
    <>
      <LoginModal isOpen={loginModalOpen} toggle={toggleLoginModal} dictionary={dictionary} />
      {saveModalOpen && <JobSearchSaveModal toggle={toggleSaveModal} setSaveSuccess={setSaveSuccess} dictionary={dictionary} />}
      <Col xs={12} lg={4}>
        <aside id="jobsearchContainer">
          {process.env.NEXT_PUBLIC_APP !== 'sandyou.be' && locale !== 'pl' && locale !== 'ro' && (
            <JobSearchForm
              dictionary={dictionary}
              formSubmitHandler={(newInputs) => {
                push(
                  `/${locale}/jobs/?${querystring.stringify({ ...vacancyParams, ...newInputs, page: 1, sort: vacancyParams.sort[0] || 'score' }, { skipEmptyString: true, skipNull: true })}`
                );
              }}
            />
          )}
          {jobData && jobData?.count > 0 && (
            <div className="d-none d-lg-block mt-3">
              <JobFacetFilters open facets={jobData.facets} dictionary={dictionary} />
            </div>
          )}
          <Button
            outline
            onClick={handleClickSave}
            className="jobs-button aside d-none d-lg-flex align-items-center justify-content-center mt-3 mb-5"
          >
            <MdOutlineSave className="me-2" />
            <div>{formatMessage({ id: 'save_jobsearch' })}</div>
          </Button>
        </aside>
      </Col>
      <Col xs={12} lg={8} className="jobResults">
        <Row
          id="resultCount"
          className="d-flex justify-content-between align-items-md-center flex-column flex-md-row mt-3 mt-lg-0 mb-3"
        >
          {jobData?.count > 0 && (
            <Col xs={12} lg={4} xl={5} xxl={6} className="d-flex justify-content-center justify-content-lg-start mb-1 mb-lg-0">
              <h2 className="my-3 my-lg-0">
                {firstJob} - {lastJob} {formatMessage({ id: 'of' })} <span id="count">{jobData?.count}</span>{' '}
                {formatMessage({ id: 'jobs-found' })}
              </h2>
            </Col>
          )}
          <Col xs={12} lg={8} xl={7} xxl={6} className="d-flex justify-content-center mt-2 mt-lg-0 px-0 align-items-center">
            <Row className="d-flex align-items-center w-100 gx-3">
              {(jobData?.count > 0 || filterCount > 0) && (
                <Col xs={{ size: 6, order: 1 }} className="d-block d-lg-none mb-3 mb-lg-0">
                  <Button
                    outline
                    onClick={toggleSideNav}
                    className="jobs-button d-flex align-items-center justify-content-center w-100"
                  >
                    <MdOutlineFilterAlt className="me-2 me-lg-1 me-xl-2" />
                    <div>{formatMessage({ id: 'filter' })}</div>
                    {filterCount > 0 && (
                      <span className="badge position-absolute top-0 start-100 translate-middle bg-danger rounded-circle">
                        {filterCount}
                      </span>
                    )}
                  </Button>
                </Col>
              )}
              {jobData?.count > 0 && (
                <Col xs={{ size: 6, order: 2 }} lg={{ size: 6, order: 2 }} className="mb-3 mb-lg-0">
                  <Input
                    type="select"
                    name="sort"
                    id="filterSelect"
                    className="jobs-button w-100 mb-lg-0"
                    value={searchParams.get('sort') || 'score'}
                    onChange={({ target }) =>
                      push(`/${locale}/jobs/?${querystring.stringify({ ...vacancyParams, sort: target.value, page: 1 })}`)
                    }
                  >
                    <option value="score">{formatMessage({ id: 'jobs-sort-filter-score' })}</option>
                    <option value="updateDate">{formatMessage({ id: 'jobs-sort-filter-update-date' })}</option>
                  </Input>
                </Col>
              )}
              <Col
                xs={{ size: jobData?.count === 0 && filterCount > 0 ? 6 : 12, order: 3 }}
                lg={{ size: 6, order: 1 }}
                className="mb-3 mb-lg-0"
              >
                <Button
                  outline
                  onClick={handleClickSave}
                  className={`jobs-button me-2 me-lg-1 me-xl-3 d-flex align-items-center justify-content-center w-100 ${
                    jobData?.count === 0 ? 'd-lg-none' : ''
                  }`}
                >
                  <MdOutlineSave className="me-2 me-lg-1 me-xl-2" />
                  <div className="text-nowrap">{formatMessage({ id: 'save_jobsearch' })}</div>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <JobsearchSavedAlert isOpen={saveSuccess} toggle={() => setSaveSuccess((prev) => !prev)} dictionary={dictionary} />

        <FilterSummary
          className={`d-none d-lg-flex border-top ${jobData?.count > 0 ? '' : 'border-bottom'}`}
          toggleSideNav={toggleSideNav}
          dictionary={dictionary}
        />

        {jobData?.count && jobData?.matches ? (
          <ListGroup>
            {jobData?.matches.map((job) => <JobTeaser job={job} key={job.id} titleTag="h3" dictionary={dictionary} />)}
          </ListGroup>
        ) : (
          <Row style={{ margin: 'auto' }} id="noResults">
            <Col lg={{ size: 6 }} className="d-flex justify-content-center align-items-center my-5 py-lg-3">
              <NoResults />
            </Col>
            <Col lg={{ size: 6 }} className="d-flex justify-content-center align-items-center mt-lg-5 mb-5">
              <div className="mx-lg-3">
                <h1 className="h3">{formatMessage({ id: 'no_job_results_title' })}</h1>
                <div className="mt-3">{formatMessage({ id: 'no_job_results_text' })}</div>
              </div>
            </Col>
          </Row>
        )}
        <Pager
          currentPage={currentPage}
          className="mt-3"
          totalPages={jobData ? Math.ceil(jobData.count / jobData.maxHits) : 0}
          changePage={(newPage) => push(`/${locale}/jobs/?${querystring.stringify({ ...vacancyParams, page: newPage })}`)}
        />
      </Col>
      <FacetSideNav isOpen={facetSideNavOpen} toggle={toggleSideNav} jobData={jobData} dictionary={dictionary} />
    </>
  );
}

export default Jobs;
